import { z } from 'zod';

import type {
  CreateCashVaultIntakeRequest,
  CreateCashVaultIntakeResponse,
} from './cash-vault-intake.dto.js';
import { CASH_DENOMINATION_ALLOCATION_TOTAL_MISMATCH } from './cash-error-codes.js';
import { CashCustodyReceiptDtoSchema } from './cash-custody-receipts.schema.js';
import { CashDenominationRowsSchema } from './cash-denominations.schema.js';
import { CashFundingSourceTypeSchema, CashMovementDtoSchema } from './cash-movement.schema.js';
import { CashPositionItemDtoSchema } from './cash-position.schema.js';
import type { CashMovementDto } from './cash-movement.dto.js';
import type { CashPositionItemDto } from './cash-position.dto.js';

const timestampSchema = z.iso.datetime();

const VaultIntakeMovementDtoSchema: z.ZodType<CashMovementDto> = CashMovementDtoSchema.superRefine(
  (value, ctx) => {
    if (value.movementKind !== 'funding') {
      ctx.addIssue({
        code: 'custom',
        message: 'cashMovement.movementKind must be "funding"',
        path: ['movementKind'],
      });
    }

    if (value.direction !== 'in') {
      ctx.addIssue({
        code: 'custom',
        message: 'cashMovement.direction must be "in"',
        path: ['direction'],
      });
    }

    if (!value.storeId || !value.locationId || value.stationId !== null) {
      ctx.addIssue({
        code: 'custom',
        message: 'Vault intake movements must be location-vault scoped',
        path: ['cashLocationId'],
      });
    }

    if (value.cashSessionId !== null || value.transferId !== null || value.expenseId !== null) {
      ctx.addIssue({
        code: 'custom',
        message: 'Vault intake movements must not target sessions, transfers, or expenses',
        path: ['cashSessionId'],
      });
    }

    if (value.sourceType !== 'funding') {
      ctx.addIssue({
        code: 'custom',
        message: 'cashMovement.sourceType must be "funding"',
        path: ['sourceType'],
      });
    }

    if (!value.fundingSourceType) {
      ctx.addIssue({
        code: 'custom',
        message: 'cashMovement.fundingSourceType is required',
        path: ['fundingSourceType'],
      });
    }

    if (!value.fundingReference?.trim()) {
      ctx.addIssue({
        code: 'custom',
        message: 'cashMovement.fundingReference is required',
        path: ['fundingReference'],
      });
    }
  },
);

const VaultIntakeTargetBalanceDtoSchema: z.ZodType<CashPositionItemDto> =
  CashPositionItemDtoSchema.superRefine((value, ctx) => {
    if (value.kind !== 'vault') {
      ctx.addIssue({
        code: 'custom',
        message: 'targetBalance.kind must be "vault"',
        path: ['kind'],
      });
    }

    if (value.scope !== 'location') {
      ctx.addIssue({
        code: 'custom',
        message: 'targetBalance.scope must be "location"',
        path: ['scope'],
      });
    }

    if (!value.storeId || !value.locationId || value.stationId !== null) {
      ctx.addIssue({
        code: 'custom',
        message: 'targetBalance must be location-vault scoped',
        path: ['cashLocationId'],
      });
    }
  });

export const CreateCashVaultIntakeRequestSchema: z.ZodType<CreateCashVaultIntakeRequest> = z
  .object({
    companyId: z.uuid(),
    operationId: z.uuid(),
    storeId: z.uuid().nullable().optional(),
    locationId: z.uuid().nullable().optional(),
    stationId: z.uuid(),
    vaultCashLocationId: z.uuid(),
    amount: z.number().int().positive(),
    sourceType: CashFundingSourceTypeSchema,
    fundingSourceCategoryId: z.uuid().nullable().optional(),
    reference: z.string().trim().min(1).max(120),
    note: z.string().trim().min(1).max(500).nullable().optional(),
    occurredAt: timestampSchema.optional(),
    denominations: CashDenominationRowsSchema.nullable().optional(),
    replacesReceiptId: z.uuid().optional(),
  })
  .superRefine((value, context) => {
    if (value.sourceType === 'other' && !value.note?.trim()) {
      context.addIssue({
        code: 'custom',
        message: 'Note is required when vault intake source is Other.',
        path: ['note'],
      });
    }

    if (!value.denominations) {
      return;
    }

    const denominationTotalMinor = value.denominations.reduce(
      (total, row) => total + row.denom * row.count,
      0,
    );
    if (denominationTotalMinor !== value.amount) {
      context.addIssue({
        code: 'custom',
        message: 'Denomination total does not match the vault intake amount.',
        path: ['denominations'],
        params: { apiErrorCode: CASH_DENOMINATION_ALLOCATION_TOTAL_MISMATCH },
      });
    }
  });

export const CreateCashVaultIntakeResponseSchema: z.ZodType<CreateCashVaultIntakeResponse> = z
  .object({
    receipt: CashCustodyReceiptDtoSchema,
    cashMovement: VaultIntakeMovementDtoSchema,
    targetBalance: VaultIntakeTargetBalanceDtoSchema,
  })
  .superRefine((value, context) => {
    if (
      value.receipt.sourceType !== 'direct_vault_intake' ||
      !['accepted', 'corrected'].includes(value.receipt.status) ||
      value.receipt.vaultInMovementId !== value.cashMovement.id ||
      value.cashMovement.sourceId !== value.receipt.id ||
      value.cashMovement.reasonCode !== 'direct_vault_intake' ||
      value.receipt.amountMinor !== value.cashMovement.amount ||
      value.receipt.targetVaultCashLocationId !== value.cashMovement.cashLocationId ||
      value.targetBalance.cashLocationId !== value.cashMovement.cashLocationId
    ) {
      context.addIssue({
        code: 'custom',
        message: 'Vault intake receipt, movement, and target balance must describe one aggregate.',
      });
    }
  });
